import type { JSONContent } from '@tiptap/core';
import Document from '@tiptap/extension-document';
import Heading from '@tiptap/extension-heading';
import Placeholder from '@tiptap/extension-placeholder';
import Text from '@tiptap/extension-text';
import { UndoRedo } from '@tiptap/extensions';
import { EditorContent, useEditor } from '@tiptap/react';
import { useEffect, useRef } from 'react';
const TitleDocument = Document.extend({
  content: 'heading'
});
const toDoc = (value: string): JSONContent => ({
  type: 'doc',
  content: [{
    type: 'heading',
    attrs: {
      level: 1
    },
    ...(value ? {
      content: [{
        type: 'text',
        text: value
      }]
    } : {})
  }]
});
type TitleSize = 'title' | 'section';

/**
 * `title` is pinned rather than a DS token: `heading-*` tops out at 24px, under the editor's own h1,
 * and the token that is this size (`display-3`) carries its own weight and tracking. `section` names a
 * document inside a page, such as a report. Set on the node, not through a `[&_h1]:` variant —
 * Tailwind can't emit a variant of a class it never generated, and the DS type tokens are hand-written
 * CSS.
 */
const TITLE_CLASS: Record<TitleSize, string> = {
  title: 'text-[1.75rem] font-bold leading-tight text-primary',
  section: 'text-heading-3 text-primary'
};

// Tiptap's Placeholder marks the empty node with `.is-empty` and a `data-placeholder` attribute;
// this paints it without adding a node that could take a cursor.
const TITLE_STYLES = ['[&_.ProseMirror]:outline-none', '[&_.is-empty]:before:pointer-events-none [&_.is-empty]:before:float-left [&_.is-empty]:before:h-0', '[&_.is-empty]:before:text-quaternary [&_.is-empty]:before:content-[attr(data-placeholder)]'].join(' ');

// Hand-picked, so anything StarterKit would have brought has to be asked for by name — `UndoRedo` is
// here because it wasn't, and Cmd+Z fell through to the builder's graph-level undo.
export const titleExtensions = (placeholder: string, size: TitleSize = 'title') => [TitleDocument, Text, Heading.configure({
  levels: [1],
  HTMLAttributes: {
    class: TITLE_CLASS[size]
  }
}),
// A read-only viewer of an unnamed task still needs something where the name would be.
Placeholder.configure({
  placeholder,
  showOnlyWhenEditable: false
}), UndoRedo];
type TitleEditorProps = {
  value: string;
  placeholder: string;
  ariaLabel: string;
  editable: boolean;
  autoFocus?: boolean;
  size?: TitleSize;
  onChange: (value: string) => void;
  /** Enter and Down from the title hand off to whatever comes next — usually the body editor. */
  onLeave?: () => void;
};

/**
 * An editor rather than an input, so the title reads as one surface with the prose below it.
 *
 * Plain text in and out: a title that round-tripped as markdown would leak `#` and `**` into the
 * rail and into the prompt the agent is given.
 */
const TitleEditor = ({
  value,
  placeholder,
  ariaLabel,
  editable,
  autoFocus,
  size = 'title',
  onChange,
  onLeave
}: TitleEditorProps) => {
  // `handleKeyDown` is captured once when the editor is built, so it reads the handler through a ref
  // rather than closing over the first render's copy.
  const onLeaveRef = useRef(onLeave);
  onLeaveRef.current = onLeave;

  // Read once. Handing `useEditor` a fresh `content` object on every render makes it reset the
  // document itself, which lands as an edit the user didn't make — the effect below owns syncing.
  const initialDoc = useRef(toDoc(value));

  // Guards `onChange` by value rather than by timing, which covers both ways the editor reports text
  // nobody typed: tiptap v3 emits once on mount, and a programmatic `setContent` can emit too.
  // Neither changes the text; a real edit always does.
  const lastKnown = useRef(value);
  const editor = useEditor({
    editable,
    autofocus: autoFocus ? 'end' : false,
    // This mounts inside an already-hydrated app; letting tiptap render on the server trips React's
    // hydration check against the contenteditable it produces.
    immediatelyRender: false,
    extensions: titleExtensions(placeholder, size),
    content: initialDoc.current,
    editorProps: {
      attributes: {
        'aria-label': ariaLabel,
        role: 'textbox'
      },
      handleKeyDown: (view, event) => {
        // A title is one heading by schema, so Enter can't split it; hand off downward instead, the
        // way Enter from a Notion page title puts you in the body. Down does the same.
        const leaving = event.key === 'Enter' || event.key === 'ArrowDown' && !event.shiftKey;
        if (!leaving) return false;
        if (onLeaveRef.current) {
          view.dom.blur();
          onLeaveRef.current();
          return true;
        }
        // Nowhere to go, and Enter still can't split the heading, so swallow it.
        return event.key === 'Enter';
      }
    },
    onUpdate: ({
      editor: instance
    }) => {
      const next = instance.getText();
      if (next === lastKnown.current) return;
      lastKnown.current = next;
      onChange(next);
    }
  });

  // Re-sync when the name changes underneath us — Percy editing the agent mid-turn, or an undo.
  // Guarded on inequality so it can't fight the user's own typing.
  useEffect(() => {
    if (!editor || editor.getText() === value) return;
    lastKnown.current = value;
    editor.commands.setContent(toDoc(value), {
      emitUpdate: false
    });
  }, [editor, value]);
  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);
  return <EditorContent editor={editor} className={TITLE_STYLES} />;
};
export default TitleEditor;